import React, { useEffect, useMemo, useState } from "react";
import { Typography, InputLabel, Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "rsuite/DateRangePicker/styles/index.css";
import { setFilterResult } from "../../../store/slices/filterSlice";
import { GettingReadyProps, RadioOption } from "../../../interface";
import { DateRangePicker } from "rsuite";
import useIsMobile from "../../../hooks/useIsMobile.hook";
import { getRangeDate } from "../../../utils/helper";
import { useGetRefData } from "../../../containers/records/useGetRefData.hook";
import CustomModal from "./CustomModal";
import styles from "./GettingReady.module.scss";
import { DateRangeOptions, ReviewTypeOptions } from "../../../utils/constants";
import MultiSelect from "../MultiSelect";
import { getSessionData } from "../../../utils/helper";
import { InfoOutlined } from "@mui/icons-material";
import { DateRange } from "rsuite/esm/DateRangePicker";

const GettingReady: React.FC<GettingReadyProps> = ({
  open,
  refresh,
  handleFilterSubmit,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();

  const [filter, setFilter] = useState<any>({
    dateRange: "",
    reviewType: [],
    region: [],
    affiliate: [],
    startDate: "",
    endDate: "",
  });
  const navigate = useNavigate();

  const { options } = useGetRefData(filter.region, refresh);
  const { distinctRegions, affiliateNames, validAffiliates } = options;
  const userInfo = useMemo(() => getSessionData(), []);

  useEffect(() => {
    if (userInfo?.profiles && userInfo.profiles?.length > 0)
      setFilter((filter: any) => ({
        ...filter,
        reviewType: userInfo.profiles,
      }));
  }, [userInfo.profiles]);

  const handleCancel = () => {
    navigate("/welcome");
  };

  const handleSubmit = () => {
    const filterRequest = {
      reviewType: filter.reviewType,
      selectedRegions: filter.region,
      selectedAffiliates: filter.affiliate,
      dateRange: filter.dateRange,
      dateRangeText: filter.dateRange,
      startDate: filter.startDate,
      endDate: filter.endDate,
    };
    dispatch(setFilterResult(filterRequest));

    handleFilterSubmit();
  };

  const RegionOptions = distinctRegions.map(
    (item) => ({ label: item, value: item }) as RadioOption,
  );

  const AffiliateOptions = affiliateNames?.map(
    (item: any) => ({ label: item, value: item }) as RadioOption,
  );

  const isSubmitEnabled =
    filter.startDate !== "" &&
    filter.endDate !== "" &&
    filter.reviewType.length > 0 &&
    filter.region.length > 0;

  const selectConfigs = [
    {
      id: "dateRange",
      name: "dateRange",
      label: "Date range",
      value: filter.dateRange,
      options: DateRangeOptions,
      multiple: false,
      placeholder: "Select Date Range",
      htmlLabel: {
        text: "Date Range",
        required: true,
      },
    },
    {
      id: "reviewType",
      name: "reviewType",
      label: "Review Type",
      value: filter.reviewType,
      options: ReviewTypeOptions,
      disabled: true,
      multiple: true,
      placeholder: "Select Review Type",
      htmlLabel: {
        text: "Review Type",
        required: false,
      },
    },
    {
      id: "region",
      name: "region",
      label: "Region",
      value: filter.region,
      options: RegionOptions ?? [],
      multiple: true,
      autocomplete: true,
      placeholder: "Select Regions",
      htmlLabel: {
        text: "Region",
        required: true,
      },
    },
    {
      id: "affiliate",
      name: "affiliate",
      label: "Affiliate",
      value: filter.affiliate,
      options: AffiliateOptions ?? [],
      multiple: true,
      virtual: true,
      autocomplete: true,
      placeholder: "Select Affiliate",
      htmlLabel: {
        text: "Affiliate",
        required: false,
      },
    },
  ];

  const onChange = (selection: any) => {
    if (selection.target.name === "dateRange") {
      const [start, end] = getRangeDate(selection.target.value);
      setFilter((prev: any) => ({
        ...prev,
        dateRange: selection.target.value,
        startDate: start,
        endDate: end,
      }));
    } else if (selection.target.name === "customDateRange") {
      const [start, end] = selection.target.value;
      setFilter((prev: any) => ({
        ...prev,
        startDate: dayjs(start).format("YYYY-MM-DD"),
        endDate: dayjs(end).format("YYYY-MM-DD"),
      }));
    } else {
      setFilter((prev: any) => ({
        ...prev,
        [selection.target.name]: selection.target.value,
        ...(selection.target.name === "region" && {
          affiliate: validAffiliates(prev.affiliate, selection.target.value),
        }),
      }));
    }
  };

  const customDateOnChange = (value: DateRange | null) => {
    const [start, end] = value ?? ["", ""];
    if (end < start || (end === "" && start === "")) {
      setFilter((prev: any) => ({
        ...prev,
        startDate: "",
        endDate: "",
      }));
      return;
    }
    onChange({
      target: { name: "customDateRange", value },
    });
  };

  return (
    <CustomModal
      title="Getting started"
      open={open}
      closeModal={handleCancel}
      confirmButton={{
        label: "Submit",
        onClick: handleSubmit,
        disabled: !isSubmitEnabled,
      }}
      closeOnSubmit={false}
      sx={{ maxWidth: "1180px" }}
    >
      {() => (
        <div className={styles.container}>
          <Typography variant="body1" className={styles.description}>
            The below filter selections need to be made before any records can
            be displayed.
          </Typography>
          <div className={styles.filterContainer}>
            <div className={styles.filterByText}>
              <InputLabel>Filter by:</InputLabel>
            </div>
            {selectConfigs.map((config) => (
              <div key={config.id} className={styles.filterItem}>
                <InputLabel htmlFor={config.id} aria-labelledby={config?.name}>
                  {config?.htmlLabel?.text}{" "}
                  {config?.htmlLabel?.required && (
                    <span className={styles.asterisk}>*</span>
                  )}
                  {config?.htmlLabel?.text === "Review Type" && (
                    <Tooltip
                      title="Review type(s) are populated based on user roles"
                      placement="right"
                    >
                      <InfoOutlined className={styles.info} />
                    </Tooltip>
                  )}
                </InputLabel>
                <MultiSelect
                  id={config.id}
                  name={config.name}
                  label={config.label}
                  showLabel={false}
                  placeholder={config.placeholder}
                  value={config.value}
                  options={config.options}
                  onChange={onChange}
                  multiple={config.multiple}
                  isVirtualized={config.virtual}
                  isAutocomplete={config.autocomplete}
                  disabled={config.disabled}
                  testId={`test-${config.name}`}
                />
                {filter.dateRange === "custom" && config.id === "dateRange" && (
                  <DateRangePicker
                    showOneCalendar={isMobile}
                    size="lg"
                    id="date-range"
                    placeholder="Select Date Range"
                    format="yyyy-MM-dd"
                    menuStyle={{ zIndex: 9999 }}
                    placement="auto"
                    onChange={customDateOnChange}
                    ranges={[]}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default GettingReady;
